import * as React from 'react';
import { Link } from 'react-router-dom';
import ReactTable, { Column } from 'react-table';
import { Control, LocalForm } from 'react-redux-form';
import { Comment } from '../..';
import { CommentViewModel, CommentType } from '@models/Comment';
import { ModalProps } from '@common/components';
// import { Dialog } from '@common/components/dialog';
import { ConfirmDeleteComponent } from '@common/components';
import { ProfileViewModel } from '@models/Auth';
// import { hasValue, getDefaultErrorMessages } from '@common/validators';
import { dateParser } from '@common/dateParser';
import { config } from '@common/config';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { ActionCompleteComponent } from '../comment/action-complete.component';
import { TooltipComponent } from '@common/components/tooltip/tooltip.component';
import { Role, Roles } from '@models/Roles';

export interface CommentSearchComponentProperties {
  readonly message: string;
  readonly query: string | null;
  readonly commentsLoading: boolean;
  readonly commentsPageNumber: number;
  readonly commentsPageSize: number;
  readonly commentsOrderBy: string;
  readonly comments: CommentViewModel[];
  readonly commentsPageCount: number;
  readonly commentType: CommentType;
  readonly entityId: number;
  readonly currentProfile: ProfileViewModel | null;
  readonly getProfile: () => void;
  readonly getComments: (
    id: number,
    commentsType: CommentType,
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string
  ) => void;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly toggleDialog: (comment: string) => void;
  readonly clearComments: () => void;
  readonly completeComment: (oldComment: CommentViewModel, commentType: CommentType) => void;

  readonly deleteComment: (comment: CommentViewModel, commentType: CommentType) => void;
}

interface FormModel {
  readonly query: string;
}

export interface CommentSearchComponentState {
  timersArray: { id: number; time: NodeJS.Timer; expired: boolean }[];
}

// Component definition
export class CommentSearchComponent extends React.Component<
  CommentSearchComponentProperties,
  CommentSearchComponentState
> {
  // state = { selectedCommentText: '' };
  state: CommentSearchComponentState = {
    timersArray: []
  };

  private isInitialized: boolean = false;

  componentDidMount() {
    this.getComments();
    this.props.getProfile();
  }

  componentWillReceiveProps(nextProps: CommentSearchComponentProperties) {
    if (this.props.comments !== nextProps.comments) {
      this.configureTimers(nextProps.comments);
    }
  }

  componentWillUnmount() {
    for (let timer of this.state.timersArray) {
      clearTimeout(timer.time);
    }
  }

  getComments() {
    this.props.getComments(this.props.entityId, this.props.commentType, '', 10, 1, 'addedDate', 'desc');
  }

  showCommentModal = (e?: Event) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    this.props.toggleModal({
      title: 'Create New Comment',
      component: Comment,
      childProps: {
        entityId: this.props.entityId,
        commentType: this.props.commentType,
        hideSave: true
      },
      saveCallback: this.closeModal,
      closeCallback: this.props.clearComments
    });
  };

  editCommentModal = (comment: CommentViewModel) => {
    const timeCalc = this.calculateTime(comment);
    if (timeCalc.timeDiff <= timeCalc.timeCompare) {
      this.props.toggleModal({
        title: 'Edit Comment',
        component: Comment,
        childProps: {
          comment: comment,
          entityId: this.props.entityId,
          commentType: this.props.commentType,
          hideSave: true
        },
        saveCallback: this.closeModal,
        closeCallback: this.props.clearComments
      });
    } else {
      this.getComments();
      this.renderDialog('Time has elapsed on modifying this comment.');
    }
  };

  closeModal = () => {
    this.props.clearComments();
    this.props.toggleModal();
    this.getComments();
  };

  renderError() {
    if (!this.props.message) {
      return <div />;
    }
    return <div className="error">{this.props.message.toString()}</div>;
  }

  renderDialog(comment: string) {
    this.props.toggleDialog(comment);
  }

  editComment(comment: CommentViewModel) {
    this.showCommentModal();
  }

  deleteComment(comment: CommentViewModel) {
    const timeCalc = this.calculateTime(comment);
    if (timeCalc.timeDiff <= timeCalc.timeCompare) {
      const deleteConfirm = () => {
        this.props.toggleModal();
        this.props.deleteComment(comment, this.props.commentType);
        this.getComments();
      };

      this.props.toggleModal({
        title: 'Delete Comment',
        saveLabel: 'Delete',
        component: ConfirmDeleteComponent,
        isSmall: true,
        childProps: {
          comment: comment,
          entityId: this.props.entityId,
          commentsType: this.props.commentType
        },
        saveCallback: deleteConfirm
      });
    } else {
      this.getComments();
      this.renderDialog('Time has elapsed on deleting this comment.');
    }
  }

  completeAction = (comment: CommentViewModel) => {
    this.props.toggleModal({
      title: 'Complete Action Item',
      component: ActionCompleteComponent,
      childProps: {
        oldComment: comment,
        entityId: this.props.entityId,
        commentType: this.props.commentType,
        hideSave: true
      },
      saveCallback: () => {
        this.props.completeComment(comment, this.props.commentType);
      },
      // saveCallback: this.closeModal.bind(this),
      closeCallback: this.props.clearComments
    });
  };

  renderActionColumn = (comment: CommentViewModel) => {
    return comment.actionDate === null && comment.employees.length > 0 ? (
      <a href="javascript:void(0);" onClick={() => this.completeAction(comment)}>
        &nbsp; Complete Action &nbsp;
      </a>
    ) : (
      ''
    );
  };

  configureTimers = (comments: CommentViewModel[]) => {
    for (let comment of comments) {
      this.configureTimer(comment);
    }
  };

  calculateTime = (value: CommentViewModel) => {
    const currentTime = dateParser.now();
    const serverTime = dateParser.parseDateToLocalTime(value.addedDate);
    const unit = config.commentActionWindowUnit;
    const timeDiff = dateParser.diff(currentTime, serverTime, unit);
    const timeCompare = Role.CheckRoles([Roles.Admin, Roles.IBSAdmin])
      ? config.commentActionWindowDuration
      : config.commentActionWindowDurationNonAdmin;

    return {
      timeDiff,
      timeCompare
    };
  };

  configureTimer = (value: CommentViewModel) => {
    const timeCalc = this.calculateTime(value);
    if (timeCalc.timeDiff <= timeCalc.timeCompare) {
      const timerDuration = (timeCalc.timeCompare - timeCalc.timeDiff) * 60 * 1000;
      let existingEntry = this.state.timersArray.find(entry => entry.id === value.commentId);
      const timerEntry = {
        id: value.commentId,
        time: setTimeout(() => {
          let tempEntry = this.state.timersArray.find(entry => entry.id === value.commentId);
          if (tempEntry) {
            this.setState(prevState => {
              let newState = prevState;
              if (tempEntry) {
                newState.timersArray[this.state.timersArray.indexOf(tempEntry)].expired = true;
              }
              return newState;
            });
          }
        }, timerDuration),
        expired: false
      };
      if (!existingEntry) {
        this.setState(prevState => ({
          timersArray: [...prevState.timersArray, timerEntry]
        }));
      }
    }
  };

  renderCommentColumn = (value: CommentViewModel) => {
    let existingEntry = this.state.timersArray.find(entry => entry.id === value.commentId);
    const currentTimerData = existingEntry && this.state.timersArray[this.state.timersArray.indexOf(existingEntry)];
    const currentExpired = currentTimerData && currentTimerData.expired;
    if (!currentExpired) {
      return (
        <div className="row center-xs">
          <a href="javascript:void(0);" onClick={() => this.editCommentModal(value)}>
            &nbsp; Edit &nbsp;
          </a>
          &nbsp;
          <a href="javascript:void(0);" onClick={() => this.deleteComment(value)}>
            &nbsp; Delete &nbsp;
          </a>
        </div>
      );
    } else {
      return '';
    }
  };

  render() {
    const columns: Column[] = [
      {
        accessor: (d: CommentViewModel) =>
          d.comment ? (
            <a
              href="javascript:void(0);"
              className="ellipsis"
              onClick={() => {
                this.renderDialog(d.comment);
              }}
            >
              {d.comment}
            </a>
          ) : (
            'N/A'
          ),
        id: 'comment',
        Header: 'Comment',
        sortable: false
      },
      {
        accessor: (d: CommentViewModel) =>
          d.employee ? (
            <Link to={getRoute(RouteDefinitions.user.Edit, { id: d.employee.id })} target="_blank">
              {d.employee.value}
            </Link>
          ) : (
            'N/A'
          ),
        id: 'employee',
        Header: 'Logged By',
        sortable: true
      },
      {
        accessor: 'addedDate',
        Header: 'Commented On',
        Cell: (props: { value: string }) => (
          <>{dateParser.parse(dateParser.parseDateToLocalTime(props.value)).format('l LT')}</>
        ),
        sortable: true
      },
      {
        accessor: (d: CommentViewModel) => d,
        id: 'ActionRequired',
        Header: 'Action Required?',
        Cell: (props: { value: CommentViewModel }) => (
          <>
            {props.value.actionDate
              ? 'Action Complete'
              : props.value.actionDate == null && props.value.employees.length
                ? 'Yes'
                : 'No'}
          </>
        ),
        sortable: true
      },
      {
        accessor: (d: CommentViewModel) => d,
        Cell: (props: { value: CommentViewModel }) => {
          const timeCalc = this.calculateTime(props.value);
          if (timeCalc.timeDiff <= timeCalc.timeCompare) {
            return this.renderCommentColumn(props.value);
          } else {
            return '';
          }
        },
        id: 'Edit/Delete',
        Header: 'Edit/Delete',
        sortable: false
      },
      {
        accessor: (d: CommentViewModel) => d,
        Cell: (props: { value: CommentViewModel }) => {
          return this.renderActionColumn(props.value);
        },
        id: 'Action',
        Header: 'Action',
        sortable: false
      }
    ];

    return (
      <div>
        <LocalForm
          className="form-horizontal"
          hideNativeErrors={true}
          onSubmit={(props: FormModel) => this.handleSearch(props.query)}
        >
          <div className="row bottom-xs">
            <div className="col-md-12">
              <h3>Comments</h3>
            </div>
            {/* <div className="col-lg-3 mb-10">
                            <label htmlFor=".filter">Filter by</label>
                            <Control.select model=".filter">
                                <option value="">No Filter</option>
                                {columns.map((column) => <option key={column.accessor as string} value={column.accessor as string}>{column.Header}</option>)}
                            </Control.select>
                        </div> */}
            <div className="col-lg-6 mb-10">
              <div className="row bottom-xs">
                <div className="col-xs-8">
                  <label htmlFor=".query">
                    Search
                    <TooltipComponent text="Search by Comment content" side="right" />
                  </label>
                  <Control.text model=".query" />
                </div>
                <div className="col-xs-4 text-right">
                  <button className="btn btn-primary" disabled={this.props.commentsLoading} type="submit">
                    Search
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-3 mb-10 text-right">
              <button onClick={(e: any) => this.showCommentModal(e)} className="btn btn-primary">
                Create New
              </button>
            </div>
          </div>
        </LocalForm>
        <ReactTable
          onPageChange={() => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          className="-striped -highlight"
          columns={columns}
          data={this.props.comments}
          defaultPageSize={10}
          loading={this.props.commentsLoading}
          manual={true}
          onFetchData={this.handleFetchData}
          pages={this.props.commentsPageCount}
        />
      </div>
    );
  }

  private handleFetchData = (state: {
    page: number;
    pageSize: number;
    sorted: Array<{ id: string; desc: string }>;
  }): void => {
    if (this.isInitialized) {
      const orderBy = state.sorted[0] ? state.sorted[0].id : 'addedBy';
      const orderDirection = state.sorted[0] ? (state.sorted[0].desc ? 'asc' : 'desc') : 'desc';
      // const orderDesc = state.sorted ? state.sorted[0].desc : true;
      this.props.getComments(
        this.props.entityId,
        this.props.commentType,
        this.props.query,
        state.pageSize,
        state.page + 1,
        orderBy,
        orderDirection
      );
    }

    this.isInitialized = true;
  };

  private handleSearch = (query: string): void => {
    this.isInitialized = true;
    this.props.getComments(
      this.props.entityId,
      this.props.commentType,
      query,
      this.props.commentsPageSize,
      1,
      'addedDate',
      'desc'
    );
  };
}



// WEBPACK FOOTER //
// ./src/comments/_components/comments-search/comments-search.component.tsx